/* eslint react-hooks/exhaustive-deps: "warn" */

import { useRef } from 'react';

import cn from 'classnames';

import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './SectionHowItWorks.module.scss';

const ROWS: { num: string; title: string; desc: string; loop?: boolean }[][] = [
  [
    {
      num: '01',
      title: 'The company files',
      desc: 'Incorporation, accounts, officers, ownership. Filed with a government registry, under legal obligation.',
    },
    {
      num: '02',
      title: 'We collect it',
      desc: 'Pulled directly from the registry in its native format, across 400+ sources and 200+ countries.',
    },
    {
      num: '03',
      title: 'We read it',
      desc: 'OCR turns the filed document into text. Our models extract the fields — names, numbers, dates, shareholdings — and flag anything that needs a human.',
    },
    {
      num: '04',
      title: 'We translate it',
      desc: "Filings arrive in the registry's own language and script. Fields are normalised to English, with the original kept alongside.",
    },
  ],
  [
    {
      num: '05',
      title: 'We structure it',
      desc: 'Entities resolved, fields normalised, ownership walked and linked across borders.',
    },
    {
      num: '06',
      title: 'We enrich it',
      desc: 'Financials, officers, corporate linkage and screening results appended to the registry core. One record instead of five lookups.',
    },
    {
      num: '07',
      title: 'You receive it',
      desc: 'API, platform or bulk file — with the provenance attached to the record, not stored in a footnote.',
    },
    {
      num: '08',
      title: 'We keep watching',
      desc: 'The registry is monitored perpetually. When a filing changes, the record changes and you are told — not at renewal.',
      loop: true,
    },
  ],
];

export const SectionHowItWorks = () => {
  const ref = useRef<HTMLDivElement>(null);
  const visible = useIntersection(ref, { threshold: 0.1 });

  return (
    <Section>
      <div className={styles.inner}>
        <div className={styles.head}>
          <div className={styles.rail}>
            <span className={styles.eyebrow}>03</span>
            <p className={styles.railTitle}>How it works</p>
          </div>
          <div className={styles.headContent}>
            <TitleBlock
              title="From the filing cabinet to your system."
              description="Eight steps between a filing cabinet in one country and a clean, checkable record in your system. The source survives every one of them."
              align="left"
              className={styles.titleBlock}
            />
          </div>
        </div>

        <div ref={ref} className={cn(styles.pipeline, visible && styles.pipelineVisible)}>
          {ROWS.map((row, rowIdx) => (
            <div key={rowIdx} className={styles.row}>
              {row.map((step, stepIdx) => (
                <div
                  key={step.num}
                  className={cn(
                    styles.step,
                    stepIdx === row.length - 1 && styles.stepLast,
                    step.loop && styles.stepLoop,
                  )}
                >
                  <div className={styles.dot}>{step.num}</div>
                  <h3 className={styles.stepTitle}>{step.title}</h3>
                  <p className={styles.stepDesc}>{step.desc}</p>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </Section>
  );
};
